import { AxiosInstance } from 'axios';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { CustomerCare, Footer } from '@/models';
import { ApiService } from '../api/api.service';
import { RestaurantService } from '../restaurant/restaurant.service';
import { CmsLink, CmsLinkLocation, CmsLinkTarget } from '@/models/cms';
import { getCountryByHostname, isLocal } from '@/helpers';
import { AnalyticsService } from '../analytics/analytics.service';

/** This is a service to retrieve JSON from our Content Management Service */
export interface CmsService {
  getCustomerCare(restaurantId: number): Promise<CustomerCare | null>;
  getFooter(countryCode: string): Promise<Footer | null>;
}

export class AppCmsService extends ApiService implements CmsService {
  aemBaseUrl = '';

  constructor(private restaurantService: RestaurantService, httpClient: AxiosInstance, store: Store<AppState>, logger: Logger, protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  async getCustomerCare(restaurantId: number): Promise<CustomerCare | null> {
    const restaurant = await this.restaurantService.getRestaurantByNumber(restaurantId);
    const countryCode = restaurant.address.countryCode ? restaurant.address.countryCode.toLowerCase() : 'us';
    try {
      const aemFragment = (await this.httpClient.get(this.getContentFragmentUrl('customer-care', countryCode))).data;
      const elements = aemFragment[':items'].root[':items'].responsivegrid[':items'].contentfragment.elements;

      return {
        message: elements.message.value,
        linkText: elements.linkText.value,
        linkUrl: elements.linkUrl.value,
      };
    } catch (e: any) {
      this.logger.error('CustomerCare could not be retrieved from CMS', e);
      return null;
    }
  }

  async getFooter(countryCode: string): Promise<Footer | null> {
    const countryProfile = getCountryByHostname(window.location.hostname);
    if (countryProfile.countryCode.toLowerCase() !== countryCode.toLowerCase()) {
      return null;
    }

    this.aemBaseUrl = this.getAemBaseUrl(countryCode);

    try {
      const aemFooter = (await this.httpClient({ url: this.getFooterUrl(countryCode), baseURL: this.aemBaseUrl })).data;
      if (!aemFooter) {
        this.logger.error('Undefined AEM footer');
        return null;
      }

      const footerContent = JSON.parse(aemFooter.exportedFooter);

      const primaryLinks = footerContent['primary'];
      const bottomLinks = footerContent['tertiary'];
      const aboutSection = footerContent['About'];
      const connectSection = footerContent['Connect with us'];
      const appSection = footerContent['Download our app'];
      const cookiePrefs = footerContent['cookiePrefs'];

      const secondaryLinks = [
        {
          heading: 'About',
          links: this.getFooterLinksAsArray(aboutSection).map(link => this.mapLink(link)),
        },
        {
          heading: 'Connect with us',
          links: this.getFooterLinksAsArray(connectSection).map(link => this.mapLink(link, 'connectSection')),
        },
        {
          heading: 'Download our app',
          links: this.getFooterLinksAsArray(appSection).map(link => this.mapLink(link, 'appSection')),
        },
      ];

      const footer: Footer = {
        primaryLinks: this.getFooterLinksAsArray(primaryLinks).map(link => this.mapLink(link)),
        secondaryLinks,
        tertiaryLinks: this.getFooterLinksAsArray(bottomLinks).map(link => this.mapLink(link)),
        cookiePrefs: this.getFooterCookiePreferenceLink(cookiePrefs)
      };

      return footer;
    } catch (e: any) {
      this.logger.error('Footer could not be retrieved from CMS', e);
      return null;
    }
  }

  private getContentFragmentUrl(fragmentName: string, countryCode: string): string {
    let countryPath: string;
    switch (countryCode) {
      case 'ca':
        countryPath = 'ca/en';
        break;
      case 'gb':
        countryPath = 'gb/en';
        break;        
      default:
        countryPath = 'us/en';
        break;
    }

    return `/content/chipotle/${countryPath}/content-fragments/${fragmentName}.model.json`;
  }

  private getAemBaseUrl(countryCode: string): string {
    let baseUrl: string;

    // This is a PROD only fix for navigating to rootdomain since is not possible to have an AEM 301 redirect
    // Testing for www to allow the footer to be displayed in the lower environments where we have www-ENVNAME
    const isAemQualified = location.hostname.toLowerCase().startsWith('www') || isLocal();
    const originUrl = location.origin;

    switch (countryCode) {
      case 'ca':
        baseUrl = isAemQualified ? originUrl : 'https://chipotle.ca';
        break;
      case 'gb':
        baseUrl = isAemQualified ? originUrl : 'https://chipotle.co.uk';
        break;
      default:
        baseUrl = isAemQualified ? originUrl : 'https://chipotle.com';
        break;
    }

    return baseUrl;
  }

  private getFooterUrl(countryCode: string): string {
    let countryPath: string;
    switch (countryCode) {
      case 'ca':
        countryPath = 'ca/en';
        break;
      case 'gb':
        countryPath = 'gb/en';
        break;
      default:
        countryPath = 'us/en';
        break;
    }

    return `/content/chipotle/${countryPath}/global-pages/global-footer/jcr:content/root/responsivegrid/global_footer.model.json`;
  }

  private mapLink(link: AemCmsLink, section?: string): CmsLink {
    const aemLinkExp = /(\/content\/chipotle\/[a-z]{2}\/[a-z]{2})/;

    // This is a hack for the AEM 6.5 upgrade that has an issue with serializing in the prior JSON format.
    if (link.title && link.title.startsWith('/content/dam')) {
      return {
        type: section === 'appSection' ? 'icon' : 'image',
        url: link.url ? link.url.replace(aemLinkExp, '') : '',
        imageUrl: `${this.aemBaseUrl}${link.title!}`,
        altText: '',
        location: CmsLinkLocation.External,
        target: link.target == 'true' ? CmsLinkTarget.Blank : CmsLinkTarget.Self,
      };
    }

    if (link.iconURL) {
      return {
        type: 'icon',
        altText: link.altText!,
        imageUrl: link.image!,
        url: link.iconURL.replace(aemLinkExp, '')!,
        location: CmsLinkLocation.External,
        target: link.target == 'true' ? CmsLinkTarget.Blank : CmsLinkTarget.Self,
      };
    }

    if (link.targetURL) {
      return {
        type: 'image',
        url: link.targetURL.replace(aemLinkExp, '')!,
        imageUrl: link.image!,
        altText: link.altText!,
        location: CmsLinkLocation.External,
        target: link.target == 'true' ? CmsLinkTarget.Blank : CmsLinkTarget.Self,
      };
    }

    return {
      type: 'text',
      url: link.url ? link.url.replace(aemLinkExp, '') : '',
      text: link.title!,
      location: CmsLinkLocation.External,
      target: link.target == 'true' ? CmsLinkTarget.Blank : CmsLinkTarget.Self,
    };
  }

  private getFooterLinksAsArray(links: any): AemCmsLink[] {
    return links
      ? Object.keys(links)
          .map(link => links[link])
          .filter(link => link.image || link.title)
      : [];
  }

  private getFooterCookiePreferenceLink(cookiePrefs: any): CmsLink {
    return { 
      type: 'button', 
      text: cookiePrefs['buttonText'], 
      url: '', 
      target: CmsLinkTarget.Blank,
      location: CmsLinkLocation.External, 
      enabled: cookiePrefs['displayButton'] == true
    };
  }
}
